/* @jsx h */
import { computed, defineComponent, h, ref, watch, type PropType } from "vue";
import { RightSheet, cn } from "@lemmonhq/uikit";
import type { Category } from "@lemmonhq/types";

// Assign categories to a catalog entity (a service or a product).
export const CategoriesSheet = defineComponent({
  name: "CategoriesSheet",
  props: {
    /** Name of the entity being edited, shown under the title. */
    subtitle: {
      type: String,
      default: "",
    },
    /** Currently assigned categories; re-read whenever the sheet opens. */
    categories: {
      type: Array as PropType<Category[]>,
      default: () => [],
    },
    /** Whether the assigned order is meaningful and can be dragged. */
    sortable: {
      type: Boolean,
      default: true,
    },
    availableCategories: {
      type: Array as PropType<Category[]>,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    onClose: {
      type: Function as PropType<() => void>,
      required: true,
    },
    onChange: {
      type: Function as PropType<(categories: Category[]) => void>,
      required: true,
    },
  },
  setup(props) {
    const selected = ref<Category[]>([]);
    const draggedIndex = ref<number | null>(null);

    watch(
      () => props.isOpen,
      (isOpen) => {
        if (isOpen) selected.value = [...props.categories];
      },
      { immediate: true },
    );

    const remainingCategories = computed(() =>
      props.availableCategories.filter(
        (cat) => !selected.value.find((s) => s.id === cat.id),
      ),
    );

    const commit = (next: Category[]) => {
      selected.value = next;
      props.onChange(next);
    };

    const addCategory = (category: Category) => {
      commit([...selected.value, category]);
    };

    const removeCategory = (categoryId: number) => {
      commit(selected.value.filter((c) => c.id !== categoryId));
    };

    const handleDragStart = (index: number) => {
      if (!props.sortable) return;
      draggedIndex.value = index;
    };

    const handleDragOver = (e: DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex.value === null || draggedIndex.value === index) return;
      const reordered = [...selected.value];
      const [dragged] = reordered.splice(draggedIndex.value, 1);
      reordered.splice(index, 0, dragged);
      draggedIndex.value = index;
      commit(reordered);
    };

    const handleDragEnd = () => {
      draggedIndex.value = null;
    };

    return () => (
      <RightSheet
        isOpen={props.isOpen}
        title={
          <div class="min-w-0">
            <h2 class="truncate text-base font-bold text-base-content">
              Categories
            </h2>
            <p class="truncate text-xs text-base-content/45">
              {props.subtitle}
            </p>
          </div>
        }
        onClose={props.onClose}
        width="480px"
        height="calc(100dvh - 2rem)"
        disableBottomSheet
      >
        <div class="flex min-h-0 flex-1 flex-col">
          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            <p class="mb-2 text-[10px] font-bold uppercase tracking-widest text-base-content/35">
              Assigned
            </p>

            {selected.value.length ? (
              <div class="space-y-1.5">
                {selected.value.map((category, index) => (
                  <div
                    key={category.id}
                    draggable={props.sortable ? "true" : "false"}
                    onDragstart={() => handleDragStart(index)}
                    onDragover={(e) => handleDragOver(e, index)}
                    onDragend={handleDragEnd}
                    class={cn(
                      "group flex items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-3 py-2 transition-shadow",
                      draggedIndex.value === index
                        ? "opacity-50"
                        : "hover:shadow-sm",
                    )}
                  >
                    {props.sortable && (
                      <span class="material-symbols-outlined shrink-0 cursor-grab text-base text-base-content/25">
                        drag_indicator
                      </span>
                    )}
                    <span class="min-w-0 flex-1 truncate text-sm font-medium text-base-content">
                      {category.name}
                    </span>
                    <button
                      type="button"
                      class="btn btn-ghost btn-xs btn-circle shrink-0 text-base-content/30 hover:text-error"
                      title="Remove category"
                      onClick={() => removeCategory(category.id)}
                    >
                      <span class="material-symbols-outlined text-sm">
                        close
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div class="rounded-xl border border-dashed border-base-300 px-4 py-6 text-center">
                <p class="text-xs text-base-content/40">
                  No categories assigned yet
                </p>
              </div>
            )}

            <p class="mb-2 mt-6 text-[10px] font-bold uppercase tracking-widest text-base-content/35">
              Add categories
            </p>

            {remainingCategories.value.length ? (
              <div class="space-y-1.5">
                {remainingCategories.value.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    class="flex w-full items-center gap-2 rounded-lg border border-transparent px-3 py-2 text-left text-sm text-base-content/70 transition-colors hover:border-base-300 hover:bg-base-200"
                    onClick={() => addCategory(category)}
                  >
                    <span class="material-symbols-outlined text-base text-base-content/30">
                      add
                    </span>
                    <span class="min-w-0 flex-1 truncate">
                      {category.name}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p class="px-3 text-xs text-base-content/35">
                All categories are already assigned
              </p>
            )}
          </div>

          <div class="flex items-center justify-end gap-2 border-t border-base-200 bg-base-100 px-5 py-3">
            <button
              type="button"
              class="btn btn-primary btn-sm gap-1.5"
              onClick={props.onClose}
            >
              <span class="material-symbols-outlined text-base">check</span>
              Done
            </button>
          </div>
        </div>
      </RightSheet>
    );
  },
});
